import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Activity, ChevronRight, Plug, Settings2, Sparkles } from "lucide-react";
import { ASSISTANTS, ASSISTANT_IDS, DEFAULT_CONNECTOR_URL, activityMeta, isAssistantId, type AssistantConnection, type AssistantId } from "@workspace/assistants";
import { connectionFor, useAssistantActivity, useAssistantCalendar, useAssistantsOverview } from "@/hooks/use-assistants";
import { ConnectAssistantDialog } from "@/components/assistant/connect-assistant-dialog";
import { AssistantAccessDialog } from "@/components/assistant/assistant-access-dialog";
import { AssistantActivityDialog } from "@/components/assistant/assistant-activity";
import { ASSISTANTS_COMING_SOON, AssistantMark, BODY, INK, MUTED, PillButton, Tag } from "@/components/assistant/assistant-ui";

/**
 * Settings → AI assistants (AI 1). Claude and ChatGPT, each with Connect or Manage, and
 * the activity log. Sits next to Calendar, since scheduling goes through it.
 *
 * Deep links: `/settings#ai-assistants` scrolls here; `?assistant=claude` also opens what
 * Claude can see and do (the "Open UnfoldWIN settings" link in AI 16).
 */
export function AiAssistantsSection() {
  const [, setLocation] = useLocation();
  const { data: overview, isError } = useAssistantsOverview();
  const { data: events } = useAssistantActivity({ enabled: Boolean(overview?.lastActivityAt) });
  const calendar = useAssistantCalendar(overview?.calendarConnected);
  const [connectFor, setConnectFor] = useState<AssistantId | null>(null);
  const [manageFor, setManageFor] = useState<AssistantId | null>(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const deepLinked = useRef(false);

  // Once, when the overview first arrives: later refetches (after Save) mustn't reopen the dialog.
  useEffect(() => {
    if (!overview || deepLinked.current) return;
    deepLinked.current = true;
    const url = new URL(window.location.href);
    const wanted = url.searchParams.get("assistant");
    if (url.hash === "#ai-assistants" || wanted) {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (isAssistantId(wanted)) {
      url.searchParams.delete("assistant");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      if (connectionFor(overview, wanted)?.status === "connected") setManageFor(wanted);
    }
  }, [overview]);

  // While coming soon the section is static, so a failed overview fetch needn't hide it.
  if (isError && !ASSISTANTS_COMING_SOON) return null;

  const included = overview?.included ?? true;
  const managed = manageFor ? connectionFor(overview, manageFor) ?? null : null;
  const latest = events?.[0];

  return (
    <section id="ai-assistants" ref={sectionRef} className="scroll-mt-24">
      <h2 className="eyebrow mb-3 flex items-center gap-2 px-1">
        AI assistants
        {ASSISTANTS_COMING_SOON && (
              <span className="normal-case tracking-normal">
                <Tag tone="purple">Coming soon</Tag>
              </span>
            )}
      </h2>
      <div className="surface-card overflow-hidden">
        <div className="flex gap-3 bg-[rgba(233,226,246,0.35)] px-6 py-4">
          <Sparkles className="mt-0.5 size-4 shrink-0 text-[#6e53ac]" />
          <p className={`text-[13px] leading-[19px] ${BODY}`}>
            {ASSISTANTS_COMING_SOON ? (
              "Soon you'll be able to let Claude or ChatGPT see your open Next Steps and help you do them — you'll choose what each one can see and do. We're putting the finishing touches on it."
            ) : (
              <>
                Let Claude or ChatGPT see your open Next Steps and help you do them. You choose what each one can see and do.
                {!included && " AI assistants are part of Plus and Premium."}
              </>
            )}
          </p>
        </div>

        {ASSISTANT_IDS.map((assistant) => (
          <AssistantRow
            key={assistant}
            assistant={assistant}
            connection={connectionFor(overview, assistant)}
            included={included}
            onConnect={() => setConnectFor(assistant)}
            onManage={() => setManageFor(assistant)}
            onUpgrade={() => setLocation("/pricing")}
          />
        ))}

        {!ASSISTANTS_COMING_SOON && (
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex w-full items-center gap-[14px] border-t border-[rgba(47,41,37,0.08)] px-6 py-3.5 text-left transition-colors hover:bg-black/[0.02]"
          >
            <Activity className={`size-4 shrink-0 ${MUTED}`} />
            <span className={`min-w-0 flex-1 text-sm font-medium ${INK}`}>Assistant activity</span>
            <span className={`shrink-0 text-[13px] ${MUTED}`}>{latest ? activityMeta(latest).split(" · ")[0] : "None yet"}</span>
            <ChevronRight className={`size-3.5 shrink-0 ${MUTED}`} />
          </button>
        )}
      </div>

      <ConnectAssistantDialog
        assistant={connectFor}
        connectorUrl={overview?.connectorUrl ?? DEFAULT_CONNECTOR_URL}
        onOpenChange={(open) => !open && setConnectFor(null)}
      />
      <AssistantAccessDialog
        connection={managed}
        calendar={calendar}
        onOpenChange={(open) => !open && setManageFor(null)}
      />
      <AssistantActivityDialog open={activityOpen} onOpenChange={setActivityOpen} />
    </section>
  );
}

function AssistantRow({
  assistant,
  connection,
  included,
  onConnect,
  onManage,
  onUpgrade,
}: {
  assistant: AssistantId;
  connection: AssistantConnection | undefined;
  included: boolean;
  onConnect: () => void;
  onManage: () => void;
  onUpgrade: () => void;
}) {
  const name = ASSISTANTS[assistant].name;
  const status = connection?.status ?? "not_connected";

  if (ASSISTANTS_COMING_SOON) {
    return (
      <div className="flex items-center gap-[14px] border-t border-[rgba(47,41,37,0.08)] px-6 py-4">
        <AssistantMark assistant={assistant} />
        <div className="min-w-0 flex-1">
          <p className={`text-[15px] font-semibold ${INK}`}>{name}</p>
          <p className={`mt-[3px] text-xs leading-[17px] ${MUTED}`}>Connecting {name} is on its way</p>
        </div>
        <PillButton size="sm" variant="outline" disabled aria-label={`Connecting ${name} is coming soon`}>
          <Plug className="size-[15px]" />
          Coming soon
        </PillButton>
      </div>
    );
  }

  let detail = "Not connected";
  if (status === "connected" && connection?.connectedAt) detail = `Connected · since ${shortDate(connection.connectedAt)}`;
  if (status === "paused") detail = "Paused · part of Plus and Premium";
  if (status === "not_connected" && !included) detail = "Part of Plus and Premium";

  return (
    <div className="flex items-center gap-[14px] border-t border-[rgba(47,41,37,0.08)] px-6 py-4">
      <AssistantMark assistant={assistant} />
      <div className="min-w-0 flex-1">
        <p className={`text-[15px] font-semibold ${INK}`}>{name}</p>
        <p className={`mt-[3px] text-xs leading-[17px] ${MUTED}`}>{detail}</p>
      </div>
      {status === "not_connected" && included && (
        <PillButton size="sm" onClick={onConnect} aria-label={`Connect ${name}`}>
          <Plug className="size-[15px]" />
          Connect
        </PillButton>
      )}
      {status === "not_connected" && !included && (
        <PillButton size="sm" variant="outline" onClick={onUpgrade}>
          Upgrade
        </PillButton>
      )}
      {status !== "not_connected" && (
        <PillButton size="sm" variant="outline" onClick={onManage} aria-label={`Manage ${name}`}>
          <Settings2 className="size-[15px]" />
          Manage
        </PillButton>
      )}
    </div>
  );
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
